import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import bgImage from '../../assets/images/background/bg-7.jpg';
import { OpenAI } from 'openai';

// Define props interface
interface WorkoutPageProps {
  equipment?: string;
  formattedDiscipline?: string;
  fitnessLevel?: string;
  duration?: number;
  goal?: string;
}

// Define the training type interface
interface TrainingType {
  title: string;
}

// Define the discipline configurations type
interface DisciplineConfig {
  title: string;
  description: string;
  trainingTypes: TrainingType[];
}

// Configuration for each discipline
const disciplineConfigs: Record<string, DisciplineConfig> = {
  'crossfit': {
    title: 'CROSSFIT',
    description: 'High-intensity functional movements that combine gymnastics, weightlifting, and cardiovascular exercise.',
    trainingTypes: [
      { title: "Engine Building" },
      { title: "EMOM" },
      { title: "Chipper" },
      { title: "Open Workout" },
      { title: "Gymnastics" },
      { title: "Olympic Lifting" }
    ]
  },
  'strength-training': {
    title: 'STRENGTH TRAINING',
    description: 'Progressive resistance training to build muscular strength, endurance, and size.',
    trainingTypes: [
      { title: "Upper Body" },
      { title: "Lower Body" },
      { title: "Push" },
      { title: "Pull" },
      { title: "Full Body" },
      { title: "Compound Lifts" }
    ]
  },
  'powerlifting': {
    title: 'POWERLIFTING',
    description: 'A strength sport focusing on maximum strength in the squat, bench press, and deadlift.',
    trainingTypes: [
      { title: "Squat" },
      { title: "Bench Press" },
      { title: "Deadlift" },
      { title: "Accessories" },
      { title: "Max Effort" },
      { title: "Dynamic Effort" }
    ]
  },
  'bodybuilding': {
    title: 'BODYBUILDING',
    description: 'Training focused on muscle hypertrophy, symmetry, and aesthetics.',
    trainingTypes: [
      { title: "Chest & Triceps" },
      { title: "Back & Biceps" },
      { title: "Legs & Shoulders" },
      { title: "Push/Pull/Legs" },
      { title: "Isolation Work" },
      { title: "Volume Training" }
    ]
  },
  'hyrox': {
    title: 'HYROX',
    description: 'Race format combining running with functional workouts for a total body challenge.',
    trainingTypes: [
      { title: "Running" },
      { title: "Sled Work" },
      { title: "Wall Balls" },
      { title: "Farmers Carry" },
      { title: "Burpees" },
      { title: "Full Event Training" }
    ]
  },
  'yoga': {
    title: 'YOGA',
    description: 'Mind-body practice combining physical postures, breathing techniques, and meditation.',
    trainingTypes: [
      { title: "Vinyasa Flow" },
      { title: "Power Yoga" },
      { title: "Yin Yoga" },
      { title: "Hatha Yoga" },
      { title: "Mobility Focus" },
      { title: "Restorative" }
    ]
  },
  'home-workouts': {
    title: 'HOME WORKOUTS',
    description: 'Effective routines that can be performed with minimal equipment in a home setting.',
    trainingTypes: [
      { title: "Bodyweight" },
      { title: "HIIT" },
      { title: "Dumbbell Only" },
      { title: "Band Workouts" },
      { title: "Tabata" },
      { title: "Circuit Training" }
    ]
  }
};

// System prompts for each discipline
const systemPrompts: Record<string, string> = {
  'crossfit': 'You are a professional CrossFit coach who creates personalized workouts. Your workouts are detailed, safe, and effective, focusing on functional movements performed at high intensity.',
  'strength-training': 'You are a certified strength and conditioning specialist who creates personalized strength training programs. Your workouts are detailed, progressive, and focus on building functional strength safely and effectively.',
  'powerlifting': 'You are an experienced powerlifting coach who specializes in creating programs for the squat, bench press, and deadlift. Your workouts are technical, focused on proper form, and designed to build maximum strength.',
  'bodybuilding': 'You are a professional bodybuilding coach who creates hypertrophy-focused workouts. Your programs emphasize muscle development, symmetry, and aesthetic physique enhancement through targeted training.',
  'hyrox': 'You are a specialized HYROX coach who creates training plans for this specific race format. Your workouts combine running with functional movements to prepare athletes for the HYROX competition.',
  'yoga': 'You are an experienced yoga instructor who creates balanced yoga sequences. Your routines combine strength, flexibility, balance and breath work to create mindful movement practices.',
  'home-workouts': 'You are a home fitness specialist who creates effective workouts requiring minimal equipment. Your routines are designed to be performed in limited space while still providing challenging and effective training.'
};

const WorkoutPage: React.FC<WorkoutPageProps> = (props) => {
  const navigate = useNavigate();
  
  // Get the current path from window.location
  const currentPath = window.location.pathname.substring(1); // Remove leading slash
  
  // Determine the discipline from the URL path
  let currentDiscipline = 'crossfit'; // Default
  
  if (currentPath === 'strength-training') {
    currentDiscipline = 'strength-training';
  } else if (currentPath === 'powerlifting') {
    currentDiscipline = 'powerlifting';
  } else if (currentPath === 'bodybuilding') {
    currentDiscipline = 'bodybuilding';
  } else if (currentPath === 'hyrox') {
    currentDiscipline = 'hyrox';
  } else if (currentPath === 'yoga') {
    currentDiscipline = 'yoga';
  } else if (currentPath === 'home-workouts') {
    currentDiscipline = 'home-workouts';
  } else if (currentPath === 'crossfit') {
    currentDiscipline = 'crossfit';
  }
  
  // Get configuration for current discipline
  const config = disciplineConfigs[currentDiscipline];
  
  // Format discipline name for display
  const formattedDiscipline = currentDiscipline.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
  
  // State for tracking selected options
  const [workoutStyle, setWorkoutStyle] = useState<string>('');
  const [fitnessLevel, setFitnessLevel] = useState<string>('intermediate');
  const [duration, setDuration] = useState<number>(45);
  const [equipment, setEquipment] = useState<string>('full');
  const [goal, setGoal] = useState<string>('general');
  
  // State for form visibility and results
  const [showForm, setShowForm] = useState<boolean>(false);
  const [generating, setGenerating] = useState<boolean>(false);
  const [workout, setWorkout] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Responsive button styling
  const buttonStyle = {
    padding: '12px 20px',
    backgroundColor: 'rgba(255, 255, 255, 0.15)',
    color: 'white',
    textDecoration: 'none',
    textTransform: 'uppercase' as const,
    letterSpacing: '2px',
    fontSize: '14px',
    fontWeight: '600',
    borderRadius: '0',
    transition: 'all 0.3s ease',
    margin: '5px',
    display: 'inline-block',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    cursor: 'pointer',
    width: 'auto',
    textAlign: 'center' as const,
  };

  // Back button handler
  const handleBack = () => {
    // Navigate back to home page
    navigate('/');
  };

  // Style for the back button
  const backButtonStyle = {
    ...buttonStyle,
    position: 'absolute' as const,
    top: '20px',
    left: '20px',
    zIndex: 10,
    padding: '8px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderColor: 'rgba(255, 255, 255, 0.3)',
  };

  // Handler for saving workouts
  const handleSaveWorkout = async () => {
    // Check if user is logged in
    const user = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    
    if (!user || !token) {
      // Not logged in - redirect to profile page
      navigate('/profile');
      return;
    }

    try {
      // Parse user data to get user ID
      const userData = JSON.parse(user);
      const userId = userData._id || userData.id;

      // Prepare comprehensive workout data for backend
      const workoutData = {
        userId: userId,
        discipline: formattedDiscipline,
        workoutStyle: workoutStyle,
        date: new Date().toISOString().split('T')[0],
        content: workout,
        duration: duration,
        fitnessLevel: fitnessLevel,
        equipment: equipment,
        goal: goal
      };

      // Changed to use the correct endpoint and workoutData variable
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workouts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workoutData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to save workout');
      }

      // Successfully saved - no need to assign the response to a variable
      await response.json();
      
      // Show success message
      alert('Workout saved successfully!');
    } catch (error) {
      console.error('Error saving workout:', error);
      
      // Provide error feedback
      alert(error instanceof Error 
        ? `Failed to save workout: ${error.message}` 
        : 'Error saving workout. Please try again.'
      );
    }
  };

  // Single implementation of handleGenerateWorkout
  const handleGenerateWorkout = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      // If no token, set error message
      if (!token) {
        setError('You need to be logged in to generate workouts. Please log in and try again.');
        setGenerating(false);
        return;
      }

      // Create appropriate equipment description
      const equipmentDesc = equipment === 'full' 
        ? 'full gym setup' 
        : equipment === 'limited' 
          ? 'limited equipment (dumbbells, kettlebells, bands)' 
          : 'no equipment (bodyweight only)';

      // Prepare the data for backend API
      const workoutRequestData = {
        discipline: formattedDiscipline,
        workoutStyle: workoutStyle,
        fitnessLevel: fitnessLevel,
        duration: duration,
        equipment: equipmentDesc,
        goal: goal,
        disciplineType: currentDiscipline
      };

      // Updated to use environment variable and include Authorization header
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/workouts/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(workoutRequestData),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to generate workout');
      }

      const data = await response.json();
      
      // Set the generated workout from the backend response
      setWorkout(data.workout);
      
      // Scroll to the workout after it's generated
      setTimeout(() => {
        const resultElement = document.getElementById('workout-result');
        if (resultElement) {
          resultElement.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (err) {
      if (err instanceof Error) {
        setError(`Error: ${err.message}`);
      } else {
        setError('Failed to generate workout. Please try again later.');
      }
    } finally {
      setGenerating(false);
    }
  };

  return (
    <React.Fragment>
      {/* Back Button */}
      <button 
        onClick={handleBack}
        style={backButtonStyle}
      >
        BACK
      </button>
      
      {/* Landing Page Section */}
      <section
        className="landing-page"
        style={{
          backgroundImage: `url(${bgImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center center',
          minHeight: '100vh',
          color: 'white',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {/* Overlay for text */}
        <div className="overlay" style={{ 
          position: 'absolute', 
          top: '0', 
          left: '0', 
          width: '100%', 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.4)', 
          padding: '0 20px',
        }}>
          {/* Main Title */}
          <h1 style={{
            fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
            fontWeight: '700',
            fontSize: 'clamp(40px, 10vw, 120px)',
            fontStyle: 'italic"',
            lineHeight: '1',
            textTransform: 'uppercase' as const,
            letterSpacing: 'clamp(4px, 1.5vw, 20px)',
            color: '#ffffff',
            marginBottom: '20px',
            textShadow: '2px 2px 8px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
            width: '100%',
            padding: '0 10px',
          }}>
            {config ? config.title : formattedDiscipline.toUpperCase()}
          </h1>
          
          {/* Descriptive text */}
          <p style={{
            fontFamily: "'Montserrat', sans-serif",
            fontWeight: '400',
            fontSize: 'clamp(14px, 4vw, 16px)',
            maxWidth: '800px',
            color: '#ffffff',
            marginBottom: '30px',
            lineHeight: '1.6',
            letterSpacing: '1px',
            padding: '0 10px',
          }}>
            {config ? config.description : `Personalized ${formattedDiscipline} workouts tailored to your fitness level and goals.`}
          </p>
          
          {/* Buttons for Different Training Types */}
          <div className="training-buttons" style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '8px',
            margin: '0 auto',
            width: '100%',
            maxWidth: '600px',
            padding: '0 10px',
          }}>
            {config && config.trainingTypes ? config.trainingTypes.map((type, index) => (
              <button 
                key={index}
                onClick={() => {
                  setWorkoutStyle(type.title);
                  setShowForm(true);
                  setWorkout(null); // Clear any previous workout
                  setTimeout(() => {
                    const formElement = document.getElementById('workout-form');
                    if (formElement) {
                      window.scrollTo({ top: formElement.offsetTop - 20, behavior: 'smooth' });
                    }
                  }, 100);
                }}
                style={{
                  ...buttonStyle,
                  minWidth: '120px',
                  flex: '1 0 auto',
                  maxWidth: 'calc(50% - 10px)',
                }}
              >
                {type.title}
              </button>
            )) : (
              // Default training types if config is undefined
              ['General', 'Strength', 'Endurance', 'HIIT', 'Recovery'].map((type, index) => (
                <button 
                  key={index}
                  onClick={() => {
                    setWorkoutStyle(type);
                    setShowForm(true);
                    setWorkout(null);
                    setTimeout(() => {
                      const formElement = document.getElementById('workout-form');
                      if (formElement) {
                        window.scrollTo({ top: formElement.offsetTop - 20, behavior: 'smooth' });
                      }
                    }, 100);
                  }}
                  style={{
                    ...buttonStyle,
                    minWidth: '120px',
                    flex: '1 0 auto',
                    maxWidth: 'calc(50% - 10px)',
                  }}
                >
                  {type}
                </button>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Form Section (appears after selecting a training type) */}
      {showForm && (
        <section id="workout-form" style={{
          padding: '2rem 0',
          backgroundColor: 'black',
          color: 'white',
        }}>
          <div style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 15px'
          }}>
            <div style={{
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '0',
              padding: 'clamp(1rem, 4vw, 2rem)',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }}>
              <h2 style={{
                marginBottom: '1.5rem',
                textAlign: 'center',
                fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '3px',
                color: 'white',
                fontSize: 'clamp(20px, 5vw, 28px)',
              }}>
                {workoutStyle} WORKOUT
              </h2>
              
              {/* Fitness Level Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'clamp(12px, 3.5vw, 14px)',
                  textAlign: 'center',
                }}>
                  Fitness Level:
                </label>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  {['beginner', 'intermediate', 'advanced', 'elite'].map(level => (
                    <button
                      key={level}
                      onClick={() => setFitnessLevel(level)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: fitnessLevel === level ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                        padding: '8px 12px',
                        minWidth: '90px',
                        flex: '1 0 auto',
                        maxWidth: 'calc(50% - 8px)',
                        fontSize: 'clamp(12px, 3vw, 14px)',
                      }}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Workout Duration Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'clamp(12px, 3.5vw, 14px)',
                  textAlign: 'center',
                }}>
                  Workout Duration: {duration} minutes
                </label>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  {[20, 30, 45, 60, 90].map(time => (
                    <button
                      key={time}
                      onClick={() => setDuration(time)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: duration === time ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                        padding: '8px 12px',
                        minWidth: '60px',
                        fontSize: 'clamp(12px, 3vw, 14px)',
                      }}
                    >
                      {time} min
                    </button>
                  ))}
                </div>
              </div>

              {/* Equipment Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'clamp(12px, 3.5vw, 14px)',
                  textAlign: 'center',
                }}>
                  Available Equipment:
                </label>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  {[
                    {value: 'full', label: 'Full Gym'},
                    {value: 'limited', label: 'Limited Equipment'},
                    {value: 'none', label: 'Bodyweight Only'}
                  ].map(item => (
                    <button
                      key={item.value}
                      onClick={() => setEquipment(item.value)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: equipment === item.value ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                        padding: '8px 12px',
                        fontSize: 'clamp(12px, 3vw, 14px)',
                        flex: '1 0 auto',
                        maxWidth: 'calc(50% - 8px)',
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Goal Selection */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: 'clamp(12px, 3.5vw, 14px)',
                  textAlign: 'center',
                }}>
                  Primary Goal:
                </label>
                <div style={{
                  display: 'flex',
                  gap: '8px',
                  justifyContent: 'center',
                  flexWrap: 'wrap'
                }}>
                  {['general', 'strength', 'endurance', 'hypertrophy', 'weight loss', 'performance'].map(goalType => (
                    <button
                      key={goalType}
                      onClick={() => setGoal(goalType)}
                      style={{
                        ...buttonStyle,
                        backgroundColor: goal === goalType ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255, 255, 255, 0.1)',
                        padding: '8px 12px',
                        fontSize: 'clamp(12px, 3vw, 14px)',
                        flex: '1 0 auto',
                        maxWidth: 'calc(50% - 8px)',
                      }}
                    >
                      {goalType}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Generate Button */}
              <button 
                onClick={handleGenerateWorkout}
                disabled={generating}
                style={{
                  ...buttonStyle,
                  width: '100%',
                  backgroundColor: generating ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.25)',
                  borderColor: 'rgba(255, 255, 255, 0.3)',
                  margin: '20px 0 0 0',
                  padding: '15px 20px',
                  fontSize: 'clamp(14px, 4vw, 16px)',
                  cursor: generating ? 'not-allowed' : 'pointer',
                }}
              >
                {generating ? 'GENERATING...' : 'GENERATE WORKOUT'}
              </button>
              
              {/* Error Message */}
              {error && (
                <div style={{ 
                  backgroundColor: 'rgba(220, 53, 69, 0.1)', 
                  color: '#ff6b6b', 
                  padding: '15px', 
                  marginTop: '20px',
                  textAlign: 'center',
                  border: '1px solid rgba(220, 53, 69, 0.3)',
                  fontSize: 'clamp(12px, 3.5vw, 14px)',
                }}>
                  {error}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Generated Workout Result */}
      {workout && (
        <section id="workout-result" style={{
          padding: '2rem 0',
          backgroundColor: '#f8f9fa',
          color: '#333',
        }}>
          <div style={{
            width: '100%',
            maxWidth: '800px',
            margin: '0 auto',
            padding: '0 15px'
          }}>
            <div style={{
              border: '1px solid #ddd',
              borderRadius: '0',
              padding: 'clamp(1rem, 4vw, 2rem)',
              backgroundColor: 'white',
            }}>
              <h2 style={{
                marginBottom: '1rem',
                textAlign: 'center',
                fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '2px',
                fontSize: 'clamp(20px, 5vw, 28px)',
              }}>
                YOUR {workoutStyle} WORKOUT
              </h2>
              
              <div style={{
                whiteSpace: 'pre-wrap', 
                fontFamily: "'Montserrat', sans-serif",
                fontSize: 'clamp(14px, 3.5vw, 16px)',
                lineHeight: '1.6',
                overflowWrap: 'break-word',
              }}>
                {workout.split('\n').map((line, index) => {
                  // Convert markdown headers to HTML
                  if (line.startsWith('# ')) {
                    return <h2 key={index} style={{
                      marginTop: '1.5rem',
                      fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      fontSize: 'clamp(18px, 4.5vw, 24px)',
                    }}>{line.substring(2)}</h2>;
                  } else if (line.startsWith('## ')) {
                    return <h3 key={index} style={{
                      marginTop: '1.2rem',
                      fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                      fontWeight: '600',
                      fontSize: 'clamp(16px, 4vw, 20px)',
                    }}>{line.substring(3)}</h3>;
                  } else if (line.startsWith('### ')) {
                    return <h4 key={index} style={{
                      marginTop: '1rem',
                      fontFamily: "'Bebas Neue', 'Oswald', sans-serif",
                      fontWeight: '600',
                      fontSize: 'clamp(15px, 3.5vw, 18px)',
                    }}>{line.substring(4)}</h4>;
                  } else if (line.trim() === '') {
                    return <br key={index} />;
                  } else {
                    return <p key={index} style={{
                      fontSize: 'clamp(14px, 3.5vw, 16px)',
                      lineHeight: '1.6',
                    }}>{line}</p>;
                  }
                })}
              </div>
              
              <div style={{
                marginTop: '2rem', 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}>
                <button
                  onClick={handleSaveWorkout}
                  style={{
                    ...buttonStyle,
                    backgroundColor: '#28a745',
                    borderColor: '#28a745',
                    color: 'white',
                    width: '100%',
                    padding: '12px 20px',
                  }}
                >
                  SAVE WORKOUT
                </button>
                <button
                  onClick={() => {
                    setShowForm(true);
                    setWorkout(null);
                    const formElement = document.getElementById('workout-form');
                    if (formElement) {
                      window.scrollTo({ top: formElement.offsetTop - 20, behavior: 'smooth' });
                    }
                  }}
                  >
                  CREATE ANOTHER
                </button>
              </div>
            </div>
          </div>
        </section>
      )}
    </React.Fragment>
  );
};

export default WorkoutPage;